import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PCP_LOGGER } from '@pcp/logging';
import { Observable, catchError, defer, distinctUntilChanged, map, of, shareReplay } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { AuthServiceFacade } from './auth-service-facade.service';
import { VdoSession, VdoSessionState } from '../interfaces/vdo-session';

/** Margin before the real expiry, so the simulator is never handed a token that expires mid-load. */
const EXPIRY_MARGIN_IN_MILLIS = 10 * 60 * 1000;

/**
 * Statuses where the endpoint itself never answered - the network, a proxy or a gateway did - plus
 * the one status that asks to be retried. These do *not* latch: the next `VDO` slide rendered tries
 * again, so a dropped connection does not disable the simulator for the rest of the tab's life.
 *
 * Everything else is a definite answer from the service and latches: 503 (the documented
 * per-environment kill switch), 404 (service build predates the endpoint), 403 (predates the role
 * derivation), 500, and a 2xx with an unusable body. An App Service restart also answers 503 and so
 * latches with it - indistinguishable from the kill switch, and reloading is the recovery.
 */
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([0, 408, 429, 502, 504]);

/** Thrown for a 2xx whose body is not a usable session, so that it logs as its own status. */
class MalformedVdoSessionError extends Error {}

/** Everything one principal's session consists of. Replacing the object *is* the invalidation. */
interface SessionCache {
    session: null | VdoSession;
    request: null | Observable<VdoSessionState>;
    unavailable: boolean;
}

/**
 * Publishes the VDO simulator session for this client. Every client fetches its own; the role is
 * derived from its ClientType.
 *
 * `providedIn: 'root'` matches the sibling `SasProvider`, and keeps one cache per browser tab rather
 * than one per feature module that happens to inject it.
 */
@Injectable({ providedIn: 'root' })
export class VdoTokenProvider {
    /** Lazy; concurrent subscribers on a cold cache share one request. In memory only. */
    readonly session$: Observable<VdoSessionState> = defer(() => this.resolveSession());

    private readonly authApi = inject(AuthApiService);
    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    private cache = emptyCache();

    constructor() {
        // Logout navigates within the SPA rather than reloading, so this root singleton outlives the
        // user whose session it cached. `userId$` is stable across a token refresh, so a refresh does
        // not invalidate - which is what keeps the one-fetch-per-login contract.
        inject(AuthServiceFacade).userId$
            .pipe(distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(() => (this.cache = emptyCache()));
    }

    private resolveSession(): Observable<VdoSessionState> {
        // A request writes back into the cache object it was issued under. An invalidation swaps that
        // object out, so a late response from the previous principal updates nothing anyone reads.
        const cache = this.cache;

        if (cache.unavailable) {
            return of(UNAVAILABLE);
        }

        if (cache.session !== null && isUsable(cache.session)) {
            return of(asState(cache.session));
        }

        if (cache.request !== null) {
            return cache.request;
        }

        cache.request = this.authApi.getVdoSession().pipe(
            map(session => {
                // Checked here so that a malformed body lands in `catchError` below instead of being
                // published as a session - the renderer dereferences one unconditionally.
                assertUsableSession(session);
                cache.session = session;
                cache.request = null;
                return asState(session);
            }),
            catchError((error: unknown) => {
                cache.unavailable = !isRetryable(error);
                cache.request = null;
                if (cache === this.cache) {
                    this.logUnavailable(error);
                }
                return of(UNAVAILABLE);
            }),
            shareReplay(1),
        );

        return cache.request;
    }

    /**
     * Status only - it is the sole thing separating "no simulator here" (503) from a service build
     * without the endpoint (404), one still refusing non-teachers (403), a gateway that never
     * reached it (502/504) and a response that was not a session at all. The body is a credential.
     */
    private logUnavailable(error: unknown): void {
        this.log.trackEvent('VDO simulator unavailable', { status: statusOf(error) });
    }
}

const emptyCache = (): SessionCache => ({ session: null, request: null, unavailable: false });

const isRetryable = (error: unknown): boolean =>
    error instanceof HttpErrorResponse && RETRYABLE_STATUSES.has(error.status);

const statusOf = (error: unknown): string => {
    if (error instanceof HttpErrorResponse) {
        return String(error.status);
    }

    return error instanceof MalformedVdoSessionError ? 'malformed-response' : 'unknown';
};

/**
 * `expiresAt` is the one field this provider itself reads, and the renderer dereferences the session
 * unconditionally - so a body that fails this check must never be published as a session.
 * `simulatorBaseUrl` and `token` are deliberately not checked here: `buildVdoSimulatorUrl` already
 * validates them and renders the unavailable state, which is the same outcome by a shorter path.
 */
const assertUsableSession = (session: undefined | null | VdoSession): void => {
    if (!Number.isFinite(expiryOf(session))) {
        // No detail from the body: it is a credential even when it is malformed.
        throw new MalformedVdoSessionError('VDO session response carries no usable expiresAt');
    }
};

/** The declared `VdoSession` is a claim about the response body, not a guarantee about it. */
const expiryOf = (session: undefined | null | VdoSession): number =>
    typeof session?.expiresAt === 'string' ? new Date(session.expiresAt).getTime() : NaN;

const isUsable = (session: VdoSession): boolean =>
    expiryOf(session) - Date.now() > EXPIRY_MARGIN_IN_MILLIS;

const asState = (session: VdoSession): VdoSessionState => ({ status: 'session', session });

const UNAVAILABLE: VdoSessionState = { status: 'unavailable' };
